import * as React from "react";
import { Bar, Line, Scatter, Pie } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, ArcElement } from "chart.js";
import { IAiTableData } from "./AiSearchTypes";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, ArcElement);

interface IAiChartsProps {
    tables: IAiTableData[];
}

const chartColors = [
    "rgba(51,102,204,0.5)", "rgba(220,57,18,0.5)", "rgba(255,153,0,0.5)", "rgba(16,150,24,0.5)",
    "rgba(153,0,153,0.5)", "rgba(0,153,198,0.5)", "rgba(221,68,119,0.5)", "rgba(102,170,0,0.5)",
    "rgba(184,46,46,0.5)", "rgba(49,99,149,0.5)", "rgba(255,99,132,0.5)", "rgba(54,162,235,0.5)",
    "rgba(255,206,86,0.5)", "rgba(75,192,192,0.5)", "rgba(153,102,255,0.5)", "rgba(255,159,64,0.5)",
    "rgba(100,255,218,0.5)", "rgba(255,99,255,0.5)", "rgba(99,255,132,0.5)", "rgba(255,219,88,0.5)",
    "rgba(0,204,153,0.5)", "rgba(204,0,102,0.5)", "rgba(102,51,153,0.5)", "rgba(0,102,204,0.5)",
    "rgba(204,102,0,0.5)", "rgba(0,204,255,0.5)", "rgba(204,255,0,0.5)", "rgba(255,0,127,0.5)",
    "rgba(127,0,255,0.5)", "rgba(0,255,127,0.5)"
];

const truncateLabel = (label: any, maxLength = 12) => String(label).length > maxLength ? String(label).slice(0, maxLength) + "..." : String(label);
const looksLikeTime = (colName: any) => ["date", "time", "year", "month"].some(k => (colName || "").toLowerCase().includes(k));
const isNumber = (value: any, colName: any) => {
    if (typeof colName === "string" && colName.toLowerCase().includes("id")) return false;
    return !isNaN(Number(value)) && value !== null && value !== "";
};
const uniqueCount = (rows: any[], col: any) => new Set(rows.map(r => r[col])).size;

const chooseChart = (columns: any[], rows: any[], userQuery = "") => {
    if (!columns || !rows || rows.length === 0 || rows.length === 1) return { type: "table" };

    const sample = rows[0] || {};
    const timeCols = columns.filter(looksLikeTime);
    const numericCols = columns.filter(c => isNumber(sample[c], c));
    const catCols = columns.filter(c => !timeCols.includes(c) && !numericCols.includes(c));

    if (numericCols.length === 0) {
        return { type: "table" };
    }

    if (timeCols.length === 1 && numericCols.length === 1) {
        return { type: "line", x: timeCols[0], y: numericCols[0] };
    }

    if (catCols.length === 1 && numericCols.length >= 1) {
        for (const numCol of numericCols) {
            const total = rows.reduce((sum, r) => sum + (isNumber(r[numCol], numCol) ? Number(r[numCol]) : 0), 0);
            if ((total > 0 && total <= 1.1) || Math.abs(total - 100) < 5) {
                return { type: "pie", label: catCols[0], value: numCol };
            }
        }
    }

    if (catCols.length === 1 && numericCols.length === 1) {
        const uniqueCatCount = uniqueCount(rows, catCols[0]);
        if (uniqueCatCount <= 20) {
            return { type: "bar", x: catCols[0], y: numericCols[0] };
        }

        return { type: "table" };
    }

    if (numericCols.length >= 2) {
        const unique = (col: any) => new Set(rows.map(r => r[col])).size;
        const a = numericCols[0], b = numericCols[1];
        if (unique(a) >= 5 && unique(b) >= 5 && unique(a) >= rows.length / 2 && unique(b) >= rows.length / 2) {
            return { type: "scatter", x: a, y: b };
        }
    }

    if (catCols.length === 1 && numericCols.length === 1 && uniqueCount(rows, catCols[0]) > 20) {
        return { type: "table" };
    }

    const q = (userQuery || "").toLowerCase();
    if (q.includes("trend") || q.includes("over time")) return { type: "line" };
    if (q.includes("top") || q.includes("compare")) return { type: "bar" };

    return { type: "table" };
};

const buildLineChartData = (rows: any[], x: any, y: any) => ({
    labels: rows.map(r => String(r[x])),
    datasets: [{
        label: y,
        data: rows.map(r => Number(r[y])),
        borderColor: chartColors[0],
        backgroundColor: chartColors[0],
        fill: false,
    }]
});

const buildBarChartData = (rows: any[], x: any, y: any) => {
    const sorted = [...rows].sort((a, b) => Number(b[y] ?? 0) - Number(a[y] ?? 0)).slice(0, 20);
    return {
        labels: sorted.map(r => String(r[x])),
        datasets: [{
            label: y,
            data: sorted.map(r => Number(r[y])),
            backgroundColor: chartColors.slice(0, sorted.length),
        }]
    };
};

const buildScatterChartData = (rows: any[], x: any, y: any) => ({
    datasets: [{
        label: `${x} vs ${y}`,
        data: rows.map(r => ({ x: Number(r[x]), y: Number(r[y]) })),
        backgroundColor: chartColors[0],
    }]
});

const buildPieChartData = (rows: any[], labelCol: any, valueCol: any) => {
    const sorted = [...rows].sort((a, b) => Number(b[valueCol] ?? 0) - Number(a[valueCol] ?? 0)).slice(0, 20);
    return {
        labels: sorted.map(r => truncateLabel(r[labelCol])),
        datasets: [{
            data: sorted.map(r => Number(r[valueCol])),
            backgroundColor: chartColors.slice(0, sorted.length),
        }]
    };
};

const renderChart = (table: IAiTableData, index: number) => {
    const { dataArray, columnNames, query } = table;
    if (!Array.isArray(dataArray) || !Array.isArray(columnNames) || columnNames.length === 0) return null;

    const rows = dataArray.map((row: any) =>
        Array.isArray(row)
            ? Object.fromEntries(columnNames.map((col, i) => [col, row[i]]))
            : row
    );

    const chartRec = chooseChart(columnNames, rows, query);
    if (chartRec.type === "line") {
        return (
            <div key={`ai-line-chart-${index}`} className="aiTableWrapper">
                <Line
                    data={buildLineChartData(rows, chartRec.x, chartRec.y)}
                    options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: { legend: { display: false }, tooltip: { enabled: true } },
                        scales: {
                            x: {
                                title: { display: true, text: chartRec.x },
                                ticks: {
                                    callback: function (value) {
                                        let label = this.getLabelForValue(value as number);
                                        if (typeof label !== "string") label = String(label);
                                        return label.length > 10 ? label.slice(0, 10) + "..." : label;
                                    }
                                }
                            },
                            y: { title: { display: true, text: chartRec.y }, beginAtZero: true }
                        }
                    }}
                />
            </div>
        );
    }

    if (chartRec.type === "bar") {
        return (
            <div key={`ai-bar-chart-${index}`} className="aiTableWrapper">
                <Bar
                    data={buildBarChartData(rows, chartRec.x, chartRec.y)}
                    options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: { legend: { display: false }, tooltip: { enabled: true } },
                        scales: {
                            x: {
                                title: { display: true, text: chartRec.x },
                                ticks: {
                                    callback: function (value) {
                                        let label = this.getLabelForValue(value as number);
                                        if (typeof label !== "string") label = String(label);
                                        return label.length > 10 ? label.slice(0, 10) + "..." : label;
                                    }
                                }
                            },
                            y: { title: { display: true, text: chartRec.y }, beginAtZero: true }
                        }
                    }}
                />
            </div>
        );
    }

    if (chartRec.type === "scatter") {
        return (
            <div key={`ai-scatter-chart-${index}`} className="aiTableWrapper">
                <Scatter
                    data={buildScatterChartData(rows, chartRec.x, chartRec.y)}
                    options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: { legend: { display: false }, tooltip: { enabled: true } },
                        scales: {
                            x: { title: { display: true, text: chartRec.x }, type: "linear", position: "bottom" },
                            y: { title: { display: true, text: chartRec.y }, beginAtZero: true }
                        }
                    }}
                />
            </div>
        );
    }

    if (chartRec.type === "pie") {
        return (
            <div key={`ai-pie-chart-${index}`} className="aiTableWrapper aiPieChartFlexCenter">
                <Pie
                    data={buildPieChartData(rows, chartRec.label, chartRec.value)}
                    options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: { legend: { position: "right", display: true }, tooltip: { enabled: true } }
                    }}
                />
            </div>
        );
    }

    return null;
};

const AiCharts = ({ tables }: IAiChartsProps) => {
    return <>{tables.map(renderChart)}</>;
};

export default AiCharts;
